"use client";

/**
 * useCopyToClipboard — copy text to the clipboard and expose a transient
 * `copied` flag (auto-resets) for swapping a Copy → Check icon.
 *
 * Feedback is the caller's choice. Pass `successMessage` for the default
 * toast; omit it if the caller shows its own success state instead. On
 * failure, pass `onError` to handle it yourself (e.g. capture to Sentry
 * instead of toasting, when the text stays visible and selectable on screen
 * so there is nothing to recover from) — otherwise the default toast fires.
 * Pass `id` when more than one copyable thing shares this hook instance, so
 * `copiedId` can say which ONE was just copied rather than a single flag
 * lighting up every button on the page.
 *
 * Originally built LevDev-local (build prompt, Claude brief copy buttons)
 * and moved here to `src/hooks/` (code review, PR #719) once the Investor
 * FAQ panel — an unrelated route — was found hand-rolling the identical
 * copy-with-transient-flag logic twice in one file instead of reusing it.
 * Generalized in the same move: successMessage/onError/id are all optional
 * so the original LevDev call sites needed no behavior change beyond the
 * options-object signature.
 */
import { useCallback, useState } from "react";
import { toast } from "react-toastify";

const RESET_MS = 2000;

export function useCopyToClipboard() {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  /** Resolves true/false so a caller can run its own post-success step (e.g. analytics) without the hook needing to know about it. */
  const copy = useCallback(
    async (
      text: string,
      options?: {
        id?: string;
        successMessage?: string;
        onError?: (err: unknown) => void;
      },
    ): Promise<boolean> => {
      const id = options?.id ?? "default";
      try {
        await navigator.clipboard.writeText(text);
        setCopiedId(id);
        if (options?.successMessage) toast.success(options.successMessage);
        setTimeout(
          () => setCopiedId((current) => (current === id ? null : current)),
          RESET_MS,
        );
        return true;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        } else {
          /* benign: clipboard unavailable or blocked — fallback message provided */
          toast.error("Couldn't copy — select and copy manually");
        }
        return false;
      }
    },
    [],
  );

  return { copied: copiedId !== null, copiedId, copy };
}
